import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  type?: string;
  structuredData?: object;
}

const SEO: React.FC<SEOProps> = ({
  title = 'Crypxa - AI-Powered Cryptocurrency Prediction Platform | Smart Trading Decisions',
  description = 'Get accurate cryptocurrency price predictions powered by advanced AI and machine learning. Analyze market trends, set price alerts, compare coins, and automate trades with Crypxa\'s professional trading platform.',
  keywords = 'cryptocurrency prediction, AI trading, crypto price prediction, bitcoin prediction, ethereum prediction, trading alerts, crypto analysis, blockchain predictions, machine learning trading, automated trading bot, crypto alerts, price alerts, coin comparison, historical crypto data, candlestick pattern analysis',
  image = '/images/CRYPXA_Navbar_public_Logo.png',
  type = 'website',
  structuredData,
}) => {
  const location = useLocation();
  const baseUrl = 'https://crypxa.com';
  const url = `${baseUrl}${location.pathname}`;

  useEffect(() => {
    // Update document title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Primary Meta Tags
    updateMetaTag('title', title);
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('robots', 'index, follow');
    updateMetaTag('author', 'Crypxa');
    updateMetaTag('language', 'English');

    // Open Graph / Facebook
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:url', url, true);
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', `${baseUrl}${image}`, true);
    updateMetaTag('og:site_name', 'Crypxa', true);
    updateMetaTag('og:locale', 'en_US', true);

    // Twitter Card
    updateMetaTag('twitter:card', 'summary_large_image', true);
    updateMetaTag('twitter:url', url, true);
    updateMetaTag('twitter:title', title, true);
    updateMetaTag('twitter:description', description, true);
    updateMetaTag('twitter:image', `${baseUrl}${image}`, true);
    updateMetaTag('twitter:site', '@crypxa', true);
    updateMetaTag('twitter:creator', '@crypxa', true);

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    // Structured Data
    if (structuredData) {
      let script = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
      if (!script) {
        script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    }
  }, [title, description, keywords, image, type, url, structuredData]);

  return null;
};

export default SEO;
